/**
 * Centralized React Query key factory.
 *
 * All useQuery/useMutation calls should reference keys from here
 * instead of hardcoding strings. Enables safe refactoring and
 * invalidation across the app.
 */
export const queryKeys = {
  // Auth
  authStatus:            ["authStatus"] as const,

  // Portfolio & P&L
  portfolio:             ["portfolio"] as const,
  orders:                ["orders"] as const,
  ordersByScope:         (scope: string) => ["orders", scope] as const,
  closedPositions:       ["closed-positions"] as const,
  holdings:              ["holdings"] as const,
  chargesReport:         (scope: string) => ["chargesReport", scope] as const,
  pnlByIndex:            ["pnl-by-index"] as const,

  // Signals & Scanning
  pendingSignals:        ["pendingSignals"] as const,
  scanResults:           ["scanResults"] as const,
  signals:               ["signals"] as const,
  pendingLtp:            (key: string) => ["pendingLtp", key] as const,

  // Auto-trade toggles
  autoTradeStatus:       ["autoTradeStatus"] as const,
  autoBankniftyStatus:   ["autoBankniftyStatus"] as const,
  crashProtectionStatus: ["crashProtectionStatus"] as const,
  emergencyStopStatus:   ["emergencyStopStatus"] as const,

  // BankNifty
  bnTone:                ["bnTone"] as const,
  bnLmState:             ["bnLmState"] as const,
  bnStrategies:          ["bn_strategies"] as const,
  bnExpiries:            (index: string) => ["bn_expiries", index] as const,
  bnChain:               (index: string, expiry?: string) => ["bn_chain", index, expiry] as const,
  bnPreview:             (index: string, stratId: string) => ["bn_preview", index, stratId] as const,
  bnDirectionalState:    ["bnDirectionalState"] as const,
  chainSnapshot:         ["chain_snapshot"] as const,

  // Indices
  indicesStatus:         ["indicesStatus"] as const,

  // Health
  systemHealth:          ["systemHealth"] as const,
  tokenExpiry:           ["tokenExpiry"] as const,
  subscriptions:         ["subscriptions"] as const,
  tickDispatcher:        ["tick-dispatcher"] as const,

  // Paper trading
  paperPositions:        ["paperPositions"] as const,
  paperOrders:           ["paperOrders"] as const,

  // Settings
  settingsAll:           ["settings-all"] as const,
  allSettings:           ["allSettings"] as const,

  // Scheduler
  schedulerJobs:         ["schedulerJobs"] as const,

  // CNC
  cncEligible:           ["cncEligible"] as const,

  // Replay
  replayRuns:            ["replayRuns"] as const,
  replayConfig:          ["replayConfig"] as const,
  replayStatus:          (id: number) => ["replayStatus", id] as const,
  replayTrades:          (id: number) => ["replayTrades", id] as const,
  replayPnl:             (id: number) => ["replayPnl", id] as const,
  replayCandles:         (id: number) => ["replayCandles", id] as const,

  // Performance
  performanceSummary:    ["performanceSummary"] as const,
  performanceHistory:    ["performanceHistory"] as const,

  // GTT
  gttOrders:             ["gttOrders"] as const,
  gttStatus:             ["gttStatus"] as const,

  // Sync
  syncStatus:            ["syncStatus"] as const,
  syncHistory:           ["syncHistory"] as const,

  // Logs
  eventLogs:             ["eventLogs"] as const,
  schedulerLogs:         ["schedulerLogs"] as const,

  // Day View
  dayViewOrders:         (date: string) => ["dayViewOrders", date] as const,
  dayViewCandles:        (symbol: string) => ["dayViewCandles", symbol] as const,

  // Index chain pages (NF/FN/MC shared pattern)
  strategies:            (index: string) => ["strategies", index] as const,
  expiries:              (index: string) => ["expiries", index] as const,
  chain:                 (index: string, expiry?: string) => ["chain", index, expiry] as const,
  preview:               (index: string, stratId: string) => ["preview", index, stratId] as const,

  // Misc page-specific
  loginUrl:              ["loginUrl"] as const,
  feedStatus:            ["feedStatus"] as const,
  scanDetail:            ["scanDetail"] as const,
  scanArch:              ["scan-arch"] as const,
  allScreened:           ["allScreened"] as const,
  openingMomentum:       ["openingMomentum"] as const,
  mlModels:              ["ml-models"] as const,
  strategyMetrics:       ["strategyMetrics"] as const,
  defaultSymbols:        ["defaultSymbols"] as const,
  equityCurve:           ["equity-curve"] as const,

  // GTT
  gttActive:             ["gtt-active"] as const,
  gttOpportunities:      (minProb?: number) => minProb != null ? ["gtt-opportunities", minProb] : ["gtt-opportunities"] as const,

  // Scheduler
  jobLogs:               (jobId: string) => ["jobLogs", jobId] as const,

  // Logs
  logDates:              ["logDates"] as const,
  logStats:              (date: string) => ["logStats", date] as const,
  logEntries:            (date: string, type: string, search: string) => ["logEntries", date, type, search] as const,

  // Day View extended
  tradeSessions:         (date: string) => ["tradeSessions", date] as const,
  tradeTimeline:         (tradeId: string | number) => ["tradeTimeline", tradeId] as const,
  tradeChart:            (tradeId: string | number) => ["trade-chart", tradeId] as const,
  dayChart:              (date: string, index: string) => ["dayChart", date, index] as const,
  aiDayRuns:             (date: string, index: string) => ["aiDayRuns", date, index] as const,
  dayRunsOverlay:        (date: string, symbol: string) => ["dayRunsOverlay", date, symbol] as const,

  // Performance
  performanceHistoryBy:  (...args: (string | number)[]) => ["performance-history", ...args] as const,

  // Replay extended
  replayHistory:         ["replayHistory"] as const,
  replayDates:           ["replayDates"] as const,
  replayPersistedRuns:   ["replayPersistedRuns"] as const,
  replayLearnings:       ["replayLearnings"] as const,
  replayRunDetail:       (id: number) => ["replayRunDetail", id] as const,
  replayChain:           (index: string) => ["replayChain", index] as const,
  replayAggregate:       (n: number) => ["replayAggregate", n] as const,
  replayStatusById:      ["replayStatus"] as const,
  replayTradesList:      ["replayTrades"] as const,

  // Metrics
  metricsSummary:        ["metricsSummary"] as const,
  metricsAnomalies:      ["metricsAnomalies"] as const,

  // New ops / admin panels (2026-04-22)
  modeInfo:              ["mode-info"] as const,
  positionLifecycles:    ["position-lifecycles"] as const,
  featureFlags:          ["feature-flags"] as const,
  auditEvents:           (limit: number, strategy: string) => ["audit-events", limit, strategy] as const,
  auditCounts:           ["audit-counts"] as const,
  auditHealth:           ["audit-health"] as const,
  promotionRecs:         ["promotion-recommendations"] as const,
  shadowPnl:             (days: number) => ["shadow-pnl", days] as const,
} as const;
